import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import type { PaginatedList } from '../types/activity';
import type {
  DeleteScope,
  InstallmentsInput,
  Transaction,
  TransactionFilter,
  TransactionInput,
  TransactionSummary,
  TransferInput,
  TransferResult,
} from '../types/finance';

/** Finanças → Lançamentos (simples, parcelado, transferência). Valores em centavos com sinal. */
class TransactionService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /transactions (paginado; padrão: mês corrente, inclui previstos). */
  async list(filter: TransactionFilter = {}): Promise<PaginatedList<Transaction>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/transactions${toQuery({
        account_id: filter.account_id,
        category_id: filter.category_id,
        uncategorized: filter.uncategorized ? 1 : undefined,
        kind: filter.kind && filter.kind !== 'all' ? filter.kind : undefined,
        start: filter.start,
        end: filter.end,
        q: filter.q,
        include_projected: filter.include_projected === false ? 0 : undefined,
        schedule_status: filter.schedule_status || undefined,
        page: filter.page,
        page_size: filter.page_size,
        sort_by: filter.sort_by,
        sort_direction: filter.sort_direction,
      })}`,
    );
    return readPaginated<Transaction>(response, 'Falha ao listar os lançamentos');
  }

  /** GET /transactions/summary?start=&end=&account_id= */
  async summary(start?: string, end?: string, accountId?: number): Promise<TransactionSummary> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/transactions/summary${toQuery({ start, end, account_id: accountId })}`);
    const s = await readData<TransactionSummary>(response, 'Falha ao carregar o resumo do período');
    return { ...s, by_category: s?.by_category ?? [] };
  }

  /** GET /transactions/:id */
  async getById(id: number): Promise<Transaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/transactions/${id}`);
    return readData<Transaction>(response, 'Falha ao carregar o lançamento');
  }

  /** POST /transactions — lançamento simples. */
  async create(input: TransactionInput): Promise<Transaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/transactions', input);
    return readData<Transaction>(response, 'Falha ao criar o lançamento');
  }

  /** POST /transactions/installments — N parcelas mensais; devolve todas. */
  async createInstallments(input: InstallmentsInput): Promise<Transaction[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/transactions/installments', input);
    return readList<Transaction>(response, 'Falha ao criar o parcelamento');
  }

  /** POST /transactions/transfer — duas pernas (`from` negativa, `to` positiva). */
  async createTransfer(input: TransferInput): Promise<TransferResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/transactions/transfer', input);
    return readData<TransferResult>(response, 'Falha ao criar a transferência');
  }

  /**
   * PUT /transactions/:id. Perna de transferência: descrição, observações, data e valor sincronizam
   * a outra perna; categoria é ignorada. Parcela: edita só esta.
   */
  async update(id: number, input: TransactionInput): Promise<Transaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/transactions/${id}`, input);
    return readData<Transaction>(response, 'Falha ao atualizar o lançamento');
  }

  /**
   * POST /transactions/:id/schedule { scheduled } — marca/desmarca "agendada no banco" (Fase 7). Não
   * desvincula a linha da recorrência. Receita → 400; espelho de fatura → 409. Devolve o lançamento.
   */
  async schedule(id: number, scheduled: boolean): Promise<Transaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/transactions/${id}/schedule`, { scheduled });
    return readData<Transaction>(response, scheduled ? 'Falha ao marcar como agendada' : 'Falha ao desmarcar o agendamento');
  }

  /** DELETE /transactions/:id?scope=one|future (transferência apaga as duas pernas; espelho de fatura → 409). */
  async remove(id: number, scope: DeleteScope = 'one'): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/transactions/${id}${toQuery({ scope: scope === 'future' ? 'future' : undefined })}`);
    await ensureOk(response, 'Falha ao excluir o lançamento');
  }
}

export const transactionService = new TransactionService();
export default transactionService;
